'use client';

import { useEffect, useRef } from 'react';
import { useConsent } from '@/providers/consent-context';
import { onLCP, onINP, onCLS, onTTFB, onFCP } from 'web-vitals/attribution';

const MAX_ATTRIBUTION_LENGTH = 500;
const MAX_SOURCES_LENGTH = 2000;

function truncate(value, length) {
    if (value === null || value === undefined) return null;
    return String(value).slice(0, length);
}

function describeNode(node) {
    if (!node || node.nodeType !== Node.ELEMENT_NODE) return null;

    const element = node;
    const tagName = element.tagName?.toLowerCase() || 'element';
    const id = element.id ? `#${element.id}` : '';
    const className = typeof element.className === 'string'
        ? element.className.trim().split(/\s+/).filter(Boolean).slice(0, 3).map((name) => `.${name}`).join('')
        : '';

    return truncate(`${tagName}${id}${className}`, MAX_ATTRIBUTION_LENGTH);
}

function summarizeRect(rect) {
    if (!rect) return null;

    return [rect.x, rect.y, rect.width, rect.height]
        .map((value) => Math.round(value))
        .join(',');
}

function getClsAttribution(metric) {
    if (metric.name !== 'CLS' || !metric.attribution) return {};

    const attribution = metric.attribution;
    const sources = attribution.largestShiftEntry?.sources || [];
    const attributionSources = sources.slice(0, 3).map((source) => ({
        node:         describeNode(source.node),
        previousRect: summarizeRect(source.previousRect),
        currentRect:  summarizeRect(source.currentRect),
    }));

    return {
        attribution_target:     truncate(attribution.largestShiftTarget, MAX_ATTRIBUTION_LENGTH),
        attribution_sources:    truncate(JSON.stringify(attributionSources), MAX_SOURCES_LENGTH),
        attribution_value:      attribution.largestShiftValue ?? null,
        attribution_time:       attribution.largestShiftTime ?? null,
        attribution_load_state: truncate(attribution.loadState, 30),
    };
}

/**
 * Web Vitals RUM reporter.
 *
 * Két csatornán küld adatot:
 *  1. Saját backend (POST /system/vitals) – 100% lefedettség, consent nem szükséges
 *  2. GTM dataLayer – consent-gated (analytics kategória)
 *
 * A komponens nem renderel semmit (return null), csak useEffect-ben fut.
 * Bármely page.js-be berakható: <WebVitalsReporter pageName="..." />
 */
export default function WebVitalsReporter({ pageName = '' }) {
    const { canUseIntegration } = useConsent();
    // Ref: mindig az aktuális consent értéket tárolja, újraregisztráció nélkül
    const canGtmRef = useRef(false);

    useEffect(() => {
        canGtmRef.current = canUseIntegration('googleTagManager');
    });

    useEffect(() => {
        try {
            const backendUrl = process.env.NEXT_PUBLIC_BACKEND_URL;

            // PerformanceNavigationTiming – cache hit és nav type detektálás
            // transferSize === 0 csak böngésző HTTP-cache-t jelezne, de Cache-Control: no-store
            // miatt az sosem 0. Ehelyett a szerver válaszidőt mérjük (requestStart → responseStart):
            // < 800ms = CDN/szerver cache HIT valószínű (megegyezik a TTFB "good" küszöbével),
            // >= 800ms = origin server válasz (cache MISS vagy cold start).
            const navEntry = performance.getEntriesByType('navigation')[0] ?? null;
            const serverResponseMs = navEntry ? navEntry.responseStart - navEntry.requestStart : null;
            const cache_hit = serverResponseMs !== null ? (serverResponseMs < 800 ? 1 : 0) : null;
            const nav_type = navEntry?.type ?? null;

            // Device type detektálás (userAgentData API, fallback: viewport szélesség)
            const isMobile = navigator.userAgentData?.mobile ?? (window.innerWidth < 768);
            const device_type = isMobile ? 'mobile' : 'desktop';
            const viewport_width = window.innerWidth || null;
            const viewport_height = window.innerHeight || null;

            const page_path = window.location.pathname;

            const handleMetric = (metric) => {
                try {
                    const payload = {
                        metric:      metric.name,
                        value:       metric.value,
                        rating:      metric.rating,
                        delta:       metric.delta,
                        nav_type,
                        cache_hit,
                        device_type,
                        viewport_width,
                        viewport_height,
                        page_path,
                        page_name:   pageName,
                        source:      'rum',
                        ...getClsAttribution(metric),
                    };

                    // 1) Saját backend – mindig, consent nélkül
                    if (backendUrl) {
                        fetch(`${backendUrl}/system/vitals`, {
                            method:    'POST',
                            keepalive: true,
                            headers:   { 'Content-Type': 'application/json' },
                            body:      JSON.stringify(payload),
                        }).catch(() => {});
                    }

                    // 2) GTM dataLayer – csak consent esetén
                    if (canGtmRef.current) {
                        if (!window.dataLayer) window.dataLayer = [];
                        window.dataLayer.push({ event: 'web_vitals', ...payload });
                    }
                } catch (_) {}
            };

            onLCP(handleMetric);
            onINP(handleMetric);
            onCLS(handleMetric);
            onTTFB(handleMetric);
            onFCP(handleMetric);
        } catch (_) {}
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    return null;
}
